// Dependent-relationship options for the unified `children-dependents-overview` section
// (s147, FORMS-VALUE-SWEEP-1). SINGLE SOURCE — consumed by three sites so the option list
// and the "who is a child" rule can never drift (D-140 machine-enforcement; closes the
// duplication where shared/template-spec/children-and-dependents.ts AND
// shared/constants/capture-upstream.ts each re-declared the list):
//   1. the spec field `relationship.options.choices`  (children-and-dependents.ts)
//   2. the About-You capture-upstream mini-list        (capture-upstream.ts)
//   3. the gov-form child filter                        (estate-documents.ts EstateDocumentsData.children)
//
// 🔴 "Child of the planner" is identifiable from the value ALONE, no inference:
// CHILD_RELATIONSHIP_VALUES = { Son, Daughter } (natural OR adopted — we never map to the
// form's Natural/Adopted radio, which stays blank). Stepchild is a deliberate NON-child:
// its CPP eligibility turns on a maintenance judgment the app must never compute, so it is
// safely under-included (the executor adds a stepchild by hand). Every other value is a
// non-child dependent (Parent / Sibling closed the old "Other"-only hole).

export const DEPENDENT_RELATIONSHIP_CHOICES = [
  "Son",
  "Daughter",
  "Stepchild",
  "Grandchild",
  "Ward",
  "Parent",
  "Sibling",
  // s221 CONTACT-CARD-ONE-1 (Reade): an adult child who still relies on the planner — many parents of
  // disabled adults hold no legal guardianship, so "Ward" is not their honest answer. Deliberately NOT
  // "Son"/"Daughter": those make the interview file the row as a child (kindOf) under the under-18 rule.
  "Adult child",
  "Other dependent",
] as const;

export type DependentRelationship = (typeof DEPENDENT_RELATIONSHIP_CHOICES)[number];

/** The values that mean "a child of the planner" — the ONLY rows the gov-form child list
 *  offers. No eligibility filter of any kind; the executor still selects at generation. */
export const CHILD_RELATIONSHIP_VALUES: ReadonlySet<string> = new Set(["Son", "Daughter"]);

/** True iff a dependent row's `relationship` value marks it as the planner's child. */
export function isChildRelationship(relationship: string | null | undefined): boolean {
  return !!relationship && CHILD_RELATIONSHIP_VALUES.has(relationship.trim());
}

/** The stored value for "My adult child" (a dependant, never the children table). */
export const ADULT_CHILD_RELATIONSHIP = "Adult child";

/**
 * Who a government form lists as the deceased's CHILD (s221, Reade's ruling): a son, a daughter — AND an
 * adult child recorded as a dependant. An adult child is still the deceased's child on a government form;
 * needing care does not change that, and leaving them off is the error that hurts someone.
 * 🔴 Flagged for counsel, not closed by us. Kept apart from `isChildRelationship`, which decides which
 * TABLE a row lives in and must not move an adult dependant into the children table.
 */
export function isChildForGovernmentForms(relationship: string | null | undefined): boolean {
  if (!relationship) return false;
  const r = relationship.trim();
  return CHILD_RELATIONSHIP_VALUES.has(r) || r === ADULT_CHILD_RELATIONSHIP;
}
